import { EventType } from "@/analytics/analytics-events";
import { useCurrentProjectApiClient } from "@/components/common/project-provider/project-loading-context";
import { updateProject } from "@/components/common/project-provider/update-project";
import { ColormapOptionsForms } from "@/components/r3f/renderers/analyses/colormap-options-forms";
import { ColormapReferencePlanePanel } from "@/components/r3f/renderers/analyses/colormap-reference-plane-panel";
import { ColormapScale } from "@/components/r3f/renderers/analyses/colormap-scale";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useCanChangeTool } from "@/hooks/use-can-change-tool";
import { useSaveAnalysisAction } from "@/hooks/use-save-analysis-action";
import { useShareAnnotationOrAnalysis } from "@/hooks/use-share-annotation-or-analysis";
import { useCurrentArea } from "@/modes/mode-data-context";
import { PointCloudObject } from "@/object-cache";
import {
  selectAnalysisIsDirty,
  selectLastCreatedAnalysisId,
} from "@/store/point-cloud-analysis-tool-selector";
import {
  PointCloudAnalysis,
  removeAnalysis,
  setCadAnalysisClipBox,
  setCadDeviationDisplay,
  closeAnalysisPanel,
  setAnalysisPanelTab,
  requestAnalysisRun,
} from "@/store/point-cloud-analysis-tool-slice";
import { selectAnnotationContainer } from "@/store/selections-selectors";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import {
  activateTool,
  deactivateTool,
  setShowSpinner,
  ToolName,
} from "@/store/ui/ui-slice";
import { selectHasWritePermission } from "@/store/user-selectors";
import { selectObjectVisibility } from "@/store/view-options/view-options-selectors";
import {
  setObjectVisibility,
  ViewObjectTypes,
} from "@/store/view-options/view-options-slice";
import {
  AddLabelIcon,
  ArrowDownIcon,
  CloseCancelErrorIcon,
  CloudUploadIcon,
  cyan,
  FaroIconButton,
  FaroText,
  grey,
  ShareLinkIcon,
  useDialog,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import { selectIElement } from "@faro-lotv/project-source";
import { createMutationDeleteElement } from "@faro-lotv/service-wires";
import {
  Alert,
  Button,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState, type JSX } from "react";
import { DeleteElementDialogContent } from "../delete-element-dialog-content";
import {
  EmbeddedToolbarButton,
  EmbeddedToolbarVerticalDivider,
} from "../embedded-toolbar";
import { AnalysisViewerMenu } from "./analysis-viewer-menu";
import { CreatePdfReportDialog } from "./create-pdf-report-dialog";
import { PdfReportData, useSavePdfReport } from "./use-save-pdf-report";
import { selectClippingBox } from "@/store/clipping-box-selectors";
import CropFreeIcon from "@mui/icons-material/CropFree";
import { CadClashPanel } from "./cad-clash-panel";
import { AnalysisCreationPanel } from "./analysis-creation-panel";
import { AnalysisDockingPanel, AnalysisPanelTabs } from "./analysis-panel";
export { AnalysisDockingPanel } from "./analysis-panel";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

const toolbarButtonStyle = {
  p: 0,
  border: 0,
  ":hover *": {
    color: `${cyan[400]} !important`,
  },
};

const toolbarIconStyle = {
  height: 20,
  width: 20,
};

export type AnalysisDockingToolbarProps = {
  /** The point cloud object used by the analysis */
  pointCloud: PointCloudObject;
  /** The point cloud analysis being edited */
  analysis: PointCloudAnalysis;
};

/** @returns a PopOver to open from a menu to control the colormap view options */
export function AnalysisDockingToolbar({
  pointCloud,
  analysis,
}: AnalysisDockingToolbarProps): JSX.Element {
  const appStore = useAppStore();
  const tab =
    useAppSelector((state) => state.pointCloudAnalysisTool.panelTab) ??
    (analysis.kind === "cad" ? "cad" : "polygon");
  const dispatch = useAppDispatch();
  const client = useCurrentProjectApiClient();
  const { handleErrorWithToast } = useErrorHandlers();
  const activeTool = useAppSelector(selectActiveTool);
  const clipBox = useAppSelector(selectClippingBox);
  const cadStatus = useAppSelector(
    (state) => state.pointCloudAnalysisTool.cadStatus?.[analysis.id],
  );
  const cadReady =
    analysis.kind !== "cad" ||
    (cadStatus?.analysisId === analysis.id && cadStatus.phase === "ready");
  const clashStatus = useAppSelector(
    (state) => state.pointCloudAnalysisTool.clashStatus?.[analysis.id],
  );
  const canSave =
    cadReady ||
    (!!analysis.clashes &&
      clashStatus?.analysisId === analysis.id &&
      clashStatus.phase === "ready");
  const { area } = useCurrentArea();
  const analysisElement = useAppSelector(selectIElement(analysis.id));
  const saveAnalysis = useSaveAnalysisAction(analysis);
  function saveReadyAnalysis(): void {
    if (canSave) void saveAnalysis();
  }
  const isAnalysisDirty = useAppSelector(selectAnalysisIsDirty(analysis.id));
  // If the user has permission to edit the project or not
  const hasWritePermission = useAppSelector(selectHasWritePermission);

  const addLabels = useCallback(() => {
    if (activeTool === ToolName.analysisLabel) {
      dispatch(deactivateTool());
    } else {
      Analytics.track(EventType.openColoredAnalysisLabelCreationMode);
      dispatch(activateTool(ToolName.analysisLabel));
    }
  }, [activeTool, dispatch]);

  const hideAnalysis = useCallback(() => {
    Analytics.track(EventType.hideColoredAnalysis);
    dispatch(closeAnalysisPanel());
    dispatch(deactivateTool());
  }, [dispatch]);

  const shareAnalysis = useShareAnnotationOrAnalysis(analysisElement?.id, true);

  const { createDialog } = useDialog();

  const deleteActiveAnalysis = useCallback(async () => {
    const shouldDelete = await createDialog({
      title: "Delete Analysis?",
      confirmText: "Delete",
      content: <DeleteElementDialogContent name="the analysis" dark />,
      variant: "danger",
      dark: true,
    });

    if (!shouldDelete) {
      return;
    }

    Analytics.track(EventType.deleteColoredAnalysis);

    dispatch(setShowSpinner(true));
    const analysisElement = selectIElement(analysis.id)(appStore.getState());
    try {
      if (analysisElement) {
        const cloudElement = selectIElement(analysis.parentId)(
          appStore.getState(),
        );
        assert(
          cloudElement,
          "There must be point cloud associated to the point cloud analysis",
        );
        const annotationSection = selectAnnotationContainer(
          cloudElement,
          area,
        )(appStore.getState());
        assert(annotationSection, "There must be parent annotation section");
        await client.applyMutations([
          createMutationDeleteElement(analysisElement.id),
        ]);
        // update local copy of the project
        dispatch(
          updateProject({
            projectApi: client,
            iElementQuery: {
              ancestorIds: [annotationSection.id],
            },
          }),
        );
      }
      dispatch(removeAnalysis(analysis.id));
    } catch (error) {
      handleErrorWithToast({
        title: "Failed to delete the point cloud analysis",
        error,
      });
    }
    dispatch(setShowSpinner(false));
  }, [
    analysis.id,
    analysis.parentId,
    appStore,
    area,
    client,
    dispatch,
    handleErrorWithToast,
    createDialog,
  ]);

  const lastCreatedAnalysisId = useAppSelector(selectLastCreatedAnalysisId);
  const [editMode, setEditMode] = useState(
    lastCreatedAnalysisId === analysis.id,
  );

  const [reportData, setReportData] = useState<
    PdfReportData | string | undefined
  >();

  const [measurementVisible, setMeasurementVisible] = useState(false);
  const [isCreateReportDialogOpen, setIsCreateReportDialogOpen] =
    useState(false);

  const onCloseCreateReportDialog = useCallback(() => {
    dispatch(
      setObjectVisibility({
        type: ViewObjectTypes.measurements,
        visibility: measurementVisible,
      }),
    );
    setIsCreateReportDialogOpen(false);
  }, [dispatch, measurementVisible]);

  const savePdfReport = useSavePdfReport();

  // Save PDF report when reportImage or reportText changes
  useEffect(() => {
    async function saveReport(): Promise<void> {
      if (!reportData || typeof reportData === "string") {
        handleErrorWithToast({
          title: "Error saving PDF report",
          error: reportData ?? "Undefined error",
        });
        return;
      }
      dispatch(setShowSpinner(true));
      try {
        assert(
          reportData.imageDataUrl &&
            reportData.title &&
            reportData.analysisType,
          "report title and image are mandatory",
        );

        await savePdfReport(reportData);
      } catch (error) {
        handleErrorWithToast({
          title: "Error saving PDF report",
          error,
        });
      } finally {
        dispatch(setShowSpinner(false));
      }
    }
    if (reportData) {
      // Let React update the DOM and show the spinner before heavy work of report saving
      setTimeout(() => {
        saveReport();
      }, 0);
    }
  }, [dispatch, handleErrorWithToast, reportData, savePdfReport]);

  const canChangeTool = useCanChangeTool();
  const cadScope =
    analysis.kind === "cad" && analysis.cadReference.all
      ? "Scope: Entire active model"
      : "Scope: Selected subtree";

  return (
    <AnalysisDockingPanel fillHeight={tab === "clashes"}>
      <Stack
        direction="row"
        sx={{
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Stack direction="row" sx={{ width: "100%", alignItems: "center" }}>
          {/* Title */}
          <FaroText
            variant="labelS"
            sx={{ color: grey[0], fontWeight: "bold" }}
          >
            POINT CLOUD ANALYSIS
          </FaroText>
          <FaroIconButton
            size="s"
            margin={0}
            color={grey[0]}
            hoverColor={cyan[400]}
            sx={{ ml: "3px" }}
            onClick={() => {
              setEditMode(!editMode);
            }}
            title={editMode ? "Collapse" : "Expand"}
          >
            <ArrowDownIcon
              sx={{
                transform: editMode ? "rotate(-90deg)" : "rotate(90deg)",
                transition: "transform 0.1s linear",
              }}
            />
          </FaroIconButton>
        </Stack>
        {/* Edit mode toolbar */}
        {editMode && (
          <>
            <Stack
              direction="row"
              sx={{ gap: 0.5, justifyContent: "flex-end" }}
            >
              <Tooltip title="Add labels" placement="top">
                <EmbeddedToolbarButton
                  aria-label="add labels"
                  value="Add labels"
                  selected={activeTool === ToolName.analysisLabel}
                  onClick={addLabels}
                  sx={toolbarButtonStyle}
                  disabled={!canChangeTool || !cadReady}
                >
                  <AddLabelIcon sx={toolbarIconStyle} />
                </EmbeddedToolbarButton>
              </Tooltip>
              {isAnalysisDirty && (
                <Tooltip
                  placement="top"
                  title={
                    hasWritePermission
                      ? "Save"
                      : "Saving point cloud analysis is only accessible with edit permissions"
                  }
                >
                  <EmbeddedToolbarButton
                    aria-label="save analysis"
                    value="Save"
                    disabled={!hasWritePermission || !canSave}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      saveReadyAnalysis();
                    }}
                    sx={toolbarButtonStyle}
                  >
                    <CloudUploadIcon sx={toolbarIconStyle} />
                  </EmbeddedToolbarButton>
                </Tooltip>
              )}
              {!isAnalysisDirty && (
                <Tooltip placement="top" title="Share">
                  <EmbeddedToolbarButton
                    aria-label="share analysis"
                    value="Share"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      shareAnalysis();
                    }}
                    sx={toolbarButtonStyle}
                  >
                    <ShareLinkIcon sx={toolbarIconStyle} />
                  </EmbeddedToolbarButton>
                </Tooltip>
              )}
            </Stack>

            <EmbeddedToolbarVerticalDivider
              sx={{
                backgroundColor: grey[800],
                ml: 1,
                mr: 1,
                height: "16px",
                opacity: 1,
              }}
            />
          </>
        )}

        <AnalysisViewerMenu
          editMode={editMode}
          isAnalysisDirty={isAnalysisDirty ?? false}
          analysisElement={analysisElement}
          onDelete={deleteActiveAnalysis}
          onShare={shareAnalysis}
          onCreateReport={() => {
            if (!cadReady) return;
            const areMeasurementVisible = selectObjectVisibility(
              ViewObjectTypes.measurements,
            )(appStore.getState());
            setMeasurementVisible(areMeasurementVisible);
            dispatch(
              setObjectVisibility({
                type: ViewObjectTypes.measurements,
                visibility: false,
              }),
            );

            setIsCreateReportDialogOpen(true);
          }}
          onSave={saveReadyAnalysis}
        />
        {isCreateReportDialogOpen && (
          <CreatePdfReportDialog
            pointCloud={pointCloud}
            analysis={analysis}
            open={isCreateReportDialogOpen}
            onClose={onCloseCreateReportDialog}
            onCancel={onCloseCreateReportDialog}
            onConfirm={onCloseCreateReportDialog}
            setReportData={setReportData}
          />
        )}
        <FaroIconButton
          size="s"
          margin={0}
          color={grey[0]}
          hoverColor={cyan[400]}
          aria-label="hide analysis"
          onClick={hideAnalysis}
          title="Hide analysis"
        >
          <CloseCancelErrorIcon />
        </FaroIconButton>
      </Stack>

      <AnalysisPanelTabs
        value={tab}
        onChange={(value) => dispatch(setAnalysisPanelTab(value))}
      />
      {((analysis.kind === "cad" && tab === "polygon") ||
        (analysis.kind !== "cad" && tab !== "polygon")) && (
        <AnalysisCreationPanel embedded />
      )}
      {((analysis.kind !== "cad" && tab === "polygon") ||
        (analysis.kind === "cad" && tab === "cad")) && (
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{
            justifyContent: "center",
            flexWrap: "wrap",
            maxWidth: "100%",
          }}
        >
          {/* Edit mode controls */}
          {editMode && (
            <Stack sx={{ gap: 1, width: "190px" }}>
              {analysis.kind === "cad" ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{ color: grey[0], overflowWrap: "anywhere" }}
                  >
                    {analysis.cadReference.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: grey[0] }}>
                    {analysis.cadReference.clipBox
                      ? "Scope: Captured clip box"
                      : cadScope}
                  </Typography>
                  <Button
                    startIcon={<CropFreeIcon />}
                    disabled={!clipBox}
                    onClick={() => {
                      if (clipBox) {
                        dispatch(
                          setCadAnalysisClipBox({
                            analysisId: analysis.id,
                            clipBox: structuredClone(clipBox),
                          }),
                        );
                      }
                    }}
                  >
                    Use current clip box
                  </Button>
                  {cadStatus?.phase === "loading" && (
                    <>
                      <Typography
                        variant="caption"
                        role="status"
                        sx={{ color: grey[0] }}
                      >
                        {cadStatus.message ?? "Loading CAD reference"}
                      </Typography>
                      <LinearProgress />
                    </>
                  )}
                  {cadStatus?.analysisId === analysis.id &&
                    cadStatus.phase === "error" && (
                      <Alert severity="warning">{cadStatus.message}</Alert>
                    )}
                </>
              ) : (
                <ColormapReferencePlanePanel
                  pointCloud={pointCloud}
                  analysis={analysis}
                  width="100%"
                />
              )}
              <ColormapOptionsForms
                pointCloud={pointCloud}
                analysis={analysis}
                width="100%"
              />
              {analysis.kind === "cad" && (
                <Button
                  startIcon={<PlayArrowIcon />}
                  disabled={cadStatus?.phase === "loading"}
                  onClick={() =>
                    dispatch(
                      requestAnalysisRun({
                        analysisId: analysis.id,
                        kind: "cad",
                      }),
                    )
                  }
                >
                  Start Analysis
                </Button>
              )}
            </Stack>
          )}
          {/* Color scale */}
          <ColormapScale
            analysis={analysis}
            dark
            disabled={!hasWritePermission || !canChangeTool || !cadReady}
            onToleranceChange={
              analysis.kind === "cad" && editMode
                ? (tolerance) =>
                    dispatch(
                      setCadDeviationDisplay({
                        analysisId: analysis.id,
                        tolerance,
                        searchDistance: analysis.searchDistance,
                        deviationColouring: analysis.deviationColouring,
                      }),
                    )
                : undefined
            }
            onBandsChange={
              analysis.kind === "cad" && editMode
                ? (tolerance, outerBandLimit) => {
                    if (analysis.deviationColouring?.mode !== "bands") return;
                    dispatch(
                      setCadDeviationDisplay({
                        analysisId: analysis.id,
                        tolerance,
                        searchDistance: analysis.searchDistance,
                        deviationColouring: {
                          ...analysis.deviationColouring,
                          outerBandLimit,
                        },
                      }),
                    );
                  }
                : undefined
            }
          />
        </Stack>
      )}
      {analysis.kind === "cad" && tab === "clashes" && (
        <CadClashPanel analysis={analysis} />
      )}
    </AnalysisDockingPanel>
  );
}
